/**
 * parts.tsx — byggeklosser til inWordPress-illustrasjonene (isometrisk SVG)
 *
 * Browser: nettleservindu på toppflaten (tittellinje, innholdsblokker). Gauge:
 * speedometer på en rund sokkel. Shield: skjold. Ingen WordPress-logo eller andre
 * varemerker. Kun blå og grå.
 */

import { useId } from 'react';
import { Box, iso as heroIso } from '@/components/product/iso';
import styles from '@/components/product/illustrations.module.css';

type Project = (x: number, y: number, z: number) => string;

interface BrowserProps {
  x: number;
  y: number;
  z?: number;
  /** true: blå aksent («live»); false: grå («staging») */
  accent?: boolean;
  project?: Project;
}

/** Nettleservindu: tittellinje med tre prikker, hero-blokk, to tekstlinjer og to kort */
export function Browser({ x, y, z = 0, accent = true, project = heroIso }: BrowserProps) {
  const w = 3.8;
  const d = 2.8;
  const h = 0.22;
  const zt = z + h;
  const p = (u: number, v: number) => project(x + u * w, y + v * d, zt);
  const rect = (u0: number, v0: number, u1: number, v1: number, fill: string, key: string, opacity = 1) => (
    <polygon key={key} points={`${p(u0, v0)} ${p(u1, v0)} ${p(u1, v1)} ${p(u0, v1)}`} fill={fill} opacity={opacity} />
  );
  const bar = accent ? '#0f62fe' : '#525252';
  return (
    <g>
      <Box x={x} y={y} z={z} w={w} d={d} h={h} colors={['#ffffff', '#e0e0e0', '#c6c6c6']} project={project} />
      {rect(0, 0, 1, 0.17, bar, 'bar')}
      {[0.05, 0.1, 0.15].map((u, i) => rect(u, 0.06, u + 0.03, 0.11, '#ffffff', `d${i}`, 0.9))}
      {rect(0.08, 0.27, 0.92, 0.5, accent ? '#a6c8ff' : '#c6c6c6', 'hero')}
      {rect(0.08, 0.58, 0.6, 0.64, '#8d8d8d', 'l1', 0.8)}
      {rect(0.08, 0.7, 0.8, 0.76, '#8d8d8d', 'l2', 0.8)}
      {rect(0.08, 0.84, 0.46, 0.95, accent ? '#d0e2ff' : '#e0e0e0', 'c1')}
      {rect(0.54, 0.84, 0.92, 0.95, accent ? '#d0e2ff' : '#e0e0e0', 'c2')}
    </g>
  );
}

/** Speedometer på rund sokkel, tegnet i skjermkoordinater rundt (cx, cy) */
export function Gauge({ cx, cy }: { cx: number; cy: number }) {
  return (
    <g transform={`translate(${cx} ${cy})`}>
      <ellipse className={styles.left} cx="0" cy="12" rx="66" ry="30" />
      <ellipse className={styles.top} cx="0" cy="4" rx="66" ry="30" />
      <path d="M -46 0 A 46 46 0 0 1 46 0" fill="none" stroke="#c6c6c6" strokeWidth="12" strokeLinecap="round" />
      <path d="M -46 0 A 46 46 0 0 1 33 -32" fill="none" stroke="#0f62fe" strokeWidth="12" strokeLinecap="round" />
      <line x1="0" y1="0" x2="26" y2="-26" stroke="#393939" strokeWidth="5" strokeLinecap="round" />
      <circle cx="0" cy="0" r="8" fill="#393939" />
    </g>
  );
}

/** Skjold med lys flate (samme uttrykk som inHost-illustrasjonen) */
export function Shield({ cx, cy }: { cx: number; cy: number }) {
  const uid = useId();
  const grad = `${uid}-shield`;
  return (
    <g transform={`translate(${cx - 36} ${cy - 112}) scale(1.5)`}>
      <defs>
        <linearGradient id={grad} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#78a9ff" />
          <stop offset="1" stopColor="#0f62fe" />
        </linearGradient>
      </defs>
      <path d="M24 0 L48 8 V40 C48 62 36 74 24 80 C12 74 0 62 0 40 V8 Z" fill={`url(#${grad})`} />
      <path d="M24 8 L40 14 V40 C40 56 32 66 24 70 Z" fill="#ffffff" opacity="0.28" />
    </g>
  );
}
